"use client";

import { useEffect, useRef } from "react";

import { Text, Title } from "@mantine/core";

import { hasMotion } from "@/app/lib/motion/MotionScript";

import {
  ACTIVE_STEP_ROOT_MARGIN,
  DRILL_FROM_ATTRIBUTE,
  DRILL_TO_ATTRIBUTE,
  PLAY_ENTRY,
  STACKED_PLAY_THRESHOLD,
  STAGE_VIEW,
  StageView,
} from "./constants";
import type { StageStep } from "./definitions";
import classes from "./Stage.module.css";
import StageProvider, { useStage } from "./StageProvider";
import { revealTiming } from "./timeline";

/** The drill-in's reveal: the room opens out of the open decision's row. */
const DRILL_REVEAL_MS = 640;
const DRILL_EASING = "cubic-bezier(0.45, 0, 0.2, 1)";

/** Set on the room's frame for the length of the reveal, so it shows at once
    rather than fading in. */
const DRILLING_ATTRIBUTE = "data-drilling";

/**
 * The sticky stage: captions that scroll at the page's own speed, each with
 * the app view it describes. Where the stage fits, the views share one panel
 * pinned with `position: sticky` beside the captions, and the caption crossing
 * the viewport's middle picks the view; nothing listens to the wheel or to
 * scroll. Everywhere else, and before the client measures, each view sits
 * under its own caption. Each view plays the app's own move once, the first
 * time it shows.
 */
export default function Stage({ steps }: { steps: StageStep[] }) {
  return (
    <StageProvider>
      <StageLayout steps={steps} />
    </StageProvider>
  );
}

/**
 * Opens the room card out of the open decision's row: a clip from the row's
 * box to the card's own, so the card's type never stretches. Measured at the
 * moment the stage steps to the room, with the room's frame already showing
 * at rest, and before the layout re-renders the Decisions list away.
 */
function drillIntoRoom(decisionsFrame: HTMLElement, roomFrame: HTMLElement) {
  const row = decisionsFrame.querySelector(`[${DRILL_FROM_ATTRIBUTE}]`);
  const card = roomFrame.querySelector<HTMLElement>(`[${DRILL_TO_ATTRIBUTE}]`);

  if (!row || !card || typeof card.animate !== "function") return;

  roomFrame.setAttribute(DRILLING_ATTRIBUTE, "");

  const from = row.getBoundingClientRect();
  const to = card.getBoundingClientRect();
  const inset = [
    Math.max(0, from.top - to.top),
    Math.max(0, to.right - from.right),
    Math.max(0, to.bottom - from.bottom),
    Math.max(0, from.left - to.left),
  ];
  const ring = getComputedStyle(card).getPropertyValue("--mantine-color-blue-0").trim();

  const animation = card.animate(
    [
      {
        clipPath: `inset(${inset.map((side) => `${side}px`).join(" ")} round 8px)`,
        boxShadow: `0 0 0 3px ${ring}`,
      },
      { clipPath: "inset(0px 0px 0px 0px round 12px)", boxShadow: `0 0 0 0 ${ring}` },
    ],
    { duration: DRILL_REVEAL_MS, easing: DRILL_EASING },
  );

  const end = () => roomFrame.removeAttribute(DRILLING_ATTRIBUTE);
  animation.onfinish = end;
  animation.oncancel = end;
}

function StageLayout({ steps }: { steps: StageStep[] }) {
  const { isPinned, activeStep, setActiveStep, phaseOf, entryOf, play, registerStep, stepNode } =
    useStage();
  const frameNodes = useRef(new Map<StageView, HTMLDivElement>());
  const captionsNode = useRef<HTMLDivElement>(null);
  const lastActiveStep = useRef(activeStep);

  // Pinned: the caption at the viewport's middle picks the view, and a view
  // plays the first time it is picked.
  useEffect(() => {
    if (!isPinned) return;

    const nodes = steps.map((step) => stepNode(step.view));

    // With no caption at the middle the reader is above or below the
    // captions. Above, the stage waits on its first view; below, it keeps
    // its last.
    function settleOutsideCaptions() {
      const middle = window.innerHeight / 2;
      const first = nodes[0]?.getBoundingClientRect();
      const last = nodes[nodes.length - 1]?.getBoundingClientRect();

      if (first && first.top > middle) {
        lastActiveStep.current = 0;
        setActiveStep(0);
      } else if (last && last.bottom <= middle) {
        lastActiveStep.current = nodes.length - 1;
        setActiveStep(nodes.length - 1);
      }
    }

    function activate(step: number) {
      const previous = lastActiveStep.current;
      lastActiveStep.current = step;
      setActiveStep(step);

      // Stepping from the Decisions list to the room, the room opens out of
      // the list's open decision. Only here, only pinned, only with motion.
      const isDrill =
        hasMotion() &&
        step === previous + 1 &&
        steps[previous]?.view === STAGE_VIEW.DECISIONS &&
        steps[step].view === STAGE_VIEW.ROOM;

      const decisionsFrame = frameNodes.current.get(STAGE_VIEW.DECISIONS);
      const roomFrame = frameNodes.current.get(STAGE_VIEW.ROOM);

      if (isDrill && decisionsFrame && roomFrame) {
        drillIntoRoom(decisionsFrame, roomFrame);
      }

      // The first view is already showing when its caption arrives, so a
      // view plays on its caption's arrival, not on a change of view.
      play(steps[step].view, isDrill ? PLAY_ENTRY.DRILL : undefined);
    }

    // Observing afresh on every pin reports whichever caption is already at
    // the middle, so a reader who crosses the threshold mid-stage keeps their
    // place rather than falling back to the first view.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const step = nodes.indexOf(entry.target as HTMLDivElement);

          if (entry.isIntersecting && step >= 0) {
            activate(step);
          }
        }

        // The caption at the middle left it and none took its place: the
        // reader scrolled out of the captions, perhaps by a jump that crossed
        // none of the others (Home from mid-stage).
        if (entries.some((entry) => entry.isIntersecting)) return;

        settleOutsideCaptions();
      },
      { rootMargin: ACTIVE_STEP_ROOT_MARGIN },
    );

    // A jump from above every caption to below them, or back (End, then
    // Home from the footer), changes no caption's place at the middle, so
    // the observer above never hears of it. The panel shows only while the
    // captions are on screen, so settling whenever they come or go keeps it
    // from opening on the view the reader left.
    const captionsOnScreen = new IntersectionObserver(() => settleOutsideCaptions());

    for (const node of nodes) {
      if (node) observer.observe(node);
    }

    if (captionsNode.current) captionsOnScreen.observe(captionsNode.current);

    return () => {
      observer.disconnect();
      captionsOnScreen.disconnect();
    };
  }, [isPinned, steps, stepNode, setActiveStep, play]);

  // Stacked: a view plays once it is mostly in view under its caption.
  useEffect(() => {
    if (isPinned) return;

    const frames = frameNodes.current;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const view = (entry.target as HTMLElement).dataset.view as StageView | undefined;

          if (entry.isIntersecting && view) play(view);
        }
      },
      { threshold: STACKED_PLAY_THRESHOLD },
    );

    for (const frame of frames.values()) {
      observer.observe(frame);
    }

    return () => observer.disconnect();
  }, [isPinned, play]);

  function frameProps(step: StageStep) {
    return {
      ref: (node: HTMLDivElement | null) => {
        if (node) {
          frameNodes.current.set(step.view, node);
        } else {
          frameNodes.current.delete(step.view);
        }
      },
      className: classes.view,
      "data-view": step.view,
      "data-stage-play": phaseOf(step.view),
      "data-stage-entry": entryOf(step.view),
      style: revealTiming(entryOf(step.view)),
    };
  }

  return (
    <div className={classes.stage} data-pinned={isPinned || undefined}>
      <div ref={captionsNode}>
        {steps.map((step, index) => (
          <div
            key={step.view}
            ref={(node) => registerStep(step.view, node)}
            className={classes.step}
            data-active={(isPinned && index === activeStep) || undefined}
          >
            <div className={classes.caption}>
              <Text className={classes.kicker}>{step.kicker}</Text>
              <Title order={3} className={classes.captionTitle}>
                {step.title}
              </Title>
              <Text className={classes.captionLine}>{step.line}</Text>
            </div>

            {!isPinned && <div {...frameProps(step)}>{step.card}</div>}
          </div>
        ))}
      </div>

      {isPinned && (
        <div className={classes.pin}>
          <div className={classes.panel}>
            {/* Every view is mounted and the inactive ones fade out; inert
                keeps a hidden view's controls out of the tab order. */}
            {steps.map((step, index) => (
              <div
                key={step.view}
                {...frameProps(step)}
                data-active={index === activeStep || undefined}
                inert={index !== activeStep}
              >
                {step.card}
              </div>
            ))}

            <div className={classes.dots} aria-hidden>
              {steps.map((step, index) => (
                <span
                  key={step.view}
                  className={classes.dot}
                  data-active={index === activeStep || undefined}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
